import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Database, Search, Filter, RefreshCw, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useProjects, useAllComponents } from '../hooks/useApi';
import { componentsApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { getStatusColor } from '../utils/filters';

const ApiTestPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Use direct API hooks
  const { data: projects, loading: projectsLoading, error: projectsError } = useProjects();
  const { data: allComponents, loading: componentsLoading, error: componentsError } = useAllComponents();

  // Handle search functionality
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setSearchLoading(true);
      setSearchError(null);
      
      // Use the search function from the API service
      const results = await componentsApi.search(searchQuery);
      setSearchResults(results);
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'Search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Combine loading states
  const isLoading = projectsLoading || componentsLoading;

  // Combine errors
  const combinedError = projectsError || componentsError;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (combinedError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Error Loading API Test Page</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">{combinedError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            API Integration Test
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Test and verify the PatternLab Dashboard API integration
          </p>
        </div>

        {/* API Status */}
        <div className="card mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Database className="w-5 h-5 mr-2" />
            API Status
          </h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-lg font-medium text-green-600 dark:text-green-400">
                Status: Connected
              </span>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="btn-secondary flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {combinedError && (
          <div className="card mb-8 border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <XCircle className="w-5 h-5" />
              <span>{combinedError}</span>
            </div>
          </div>
        )}

        {projects && (
          <>
            {/* Projects Section */}
            <div className="card mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Projects ({projects.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map((project) => (
                  <div
                    key={project.name}
                    className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg"
                  >
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {project.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {project.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{project.componentCount} components</span>
                      <span>{project.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Components Section */}
            <div className="card mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Components ({allComponents?.length || 0})
              </h2>
              {componentsLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {allComponents?.slice(0, 12).map((component) => (
                    <div
                      key={`${component.projectName}-${component.name}`}
                      className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg"
                    >
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {component.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {component.description || 'No description'}
                      </p>
                                              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                          <span className={`badge ${getStatusColor(component.status || 'draft')}`}>
                            {component.status || 'draft'}
                          </span>
                          <span>{component.type || 'unknown'}</span>
                        </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Project: {component.projectName}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search Section */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Search className="w-5 h-5 mr-2" />
                Component Search
              </h2>
              <div className="flex space-x-4 mb-4">
                <input
                  type="text"
                  placeholder="Search components..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  disabled={searchLoading || !searchQuery.trim()}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Search className="w-4 h-4" />
                  <span>Search</span>
                </button>
              </div>
              
              {searchError && (
                <div className="mb-4 p-3 border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                    <XCircle className="w-4 h-4" />
                    <span>{searchError}</span>
                  </div>
                </div>
              )}
              
              {searchResults.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                    Search Results ({searchResults.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {searchResults.map((component) => (
                      <div
                        key={`${component.projectName}-${component.name}`}
                        className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg"
                      >
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                          {component.name}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {component.description || 'No description'}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                          <span className={`badge ${getStatusColor(component.status || 'draft')}`}>
                            {component.status || 'draft'}
                          </span>
                          <span>{component.type || 'unknown'}</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Project: {component.projectName}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ApiTestPage;
